import { MesfModal } from "@dexteel/mesf-core";
import { Box, Button, Typography } from "@mui/material";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  materialName: string;
  targetLayerNumber: number;
  targetMaterialNames: string[];
  reorderLayerNumber: number;
  moveIntoLayerNumber: number;
  isCommodityInTarget: boolean;
  isPending?: boolean;
  onReorder: () => void;
  onMoveInto: () => void;
  onClose: () => void;
};

const OptionPanel = ({
  title,
  description,
  preview,
  action,
}: {
  title: string;
  description: string;
  preview: string;
  action: ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1,
      p: 2,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
      {title}
    </Typography>
    <Typography variant="body2">{description}</Typography>
    <Typography variant="body2" sx={{ fontWeight: "bold", flexGrow: 1 }}>
      {preview}
    </Typography>
    {action}
  </Box>
);

export const LayerMoveDialog = ({
  open,
  materialName,
  targetLayerNumber,
  targetMaterialNames,
  reorderLayerNumber,
  moveIntoLayerNumber,
  isCommodityInTarget,
  isPending = false,
  onReorder,
  onMoveInto,
  onClose,
}: Props) => (
  <MesfModal
    title="MOVE MATERIAL"
    open={open}
    handleClose={onClose}
    maxWidth="md"
  >
    <MesfModal.Content sx={{ p: 4 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Layer {targetLayerNumber} already contains{" "}
        <strong>{targetMaterialNames.join(" + ")}</strong>. What do you want to
        do with <strong>{materialName}</strong>?
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <OptionPanel
          title="Reorder"
          description={`${materialName} becomes its own layer and the other layers shift.`}
          preview={`${materialName} → layer ${reorderLayerNumber}`}
          action={
            <Button
              variant="contained"
              color="primary"
              onClick={onReorder}
              disabled={isPending}
              autoFocus
            >
              Reorder
            </Button>
          }
        />
        <OptionPanel
          title="Move into"
          description={`${materialName} joins layer ${targetLayerNumber} and is loaded alternating with its materials.`}
          preview={
            isCommodityInTarget
              ? `${materialName} is already in layer ${targetLayerNumber}`
              : `${materialName} + ${targetMaterialNames.join(" + ")} → layer ${moveIntoLayerNumber}`
          }
          action={
            <Button
              variant="outlined"
              color="primary"
              onClick={onMoveInto}
              disabled={isPending || isCommodityInTarget}
            >
              Move into
            </Button>
          }
        />
      </Box>
    </MesfModal.Content>
  </MesfModal>
);
